import { useMemo, useState } from 'react';
import { makeChartData } from '../shared/data/charting';
import DetailList from '../shared/data/detail-list';
import EvalDisplayShell from '../shared/eval-display/EvalDisplayShell';
import EvalGraphsDisplay from '../shared/eval-display/EvalGraphsDisplay';
import { SEX_LABELS, ETHNICITY_LABELS } from '../shared/data/client-labels';
import EvalNotesPanel from './EvalNotesPanel';
import GazeHeatmapViewer from './GazeHeatmapViewer';
import CloudEvalReport from './report/CloudEvalReport';
import {
  hasUsableMaster,
  makeCloudEvalTitle,
  resolveCaseNumber,
  toLegacyMaster,
  toLegacySelectedCase,
} from './cloudEvalAdapters';

/**
 * Cloud eval display. The data is shaped differently from legacy evals
 * (see cloudEvalAdapters.js), but presents through the same shared display
 * layer so both eval types look identical where possible.
 */

const tabs = [
  { name: 'Normalized', href: '#', current: true },
  { name: 'Raw', href: '#', current: false },
  { name: 'Self-Report', href: '#', current: false },
  { name: 'Patient Info', href: '#', current: false },
  { name: 'Gaze Heatmap', href: '#', current: false },
  { name: 'Report', href: '#', current: false },
];

const PRE_SURVEY_LABELS = {
  Version: 'Version',
  version: 'Version',
  OnDrugs: 'On Drugs',
  onDrugs: 'On Drugs',
  OnMedication: 'On Medication',
  onMedication: 'On Medication',
  EducationLevel: 'Education Level',
  educationLevel: 'Education Level',
  CorrectedVision: 'Corrected Vision',
  correctedVision: 'Corrected Vision',
  HispanicLatino: 'Hispanic / Latino',
  hispanicLatino: 'Hispanic / Latino',
  MaritalStatus: 'Marital Status',
  maritalStatus: 'Marital Status',
};

const POST_SURVEY_LABELS = {
  AdultMale: 'Male Adults',
  adultMale: 'Male Adults',
  AdultFemale: 'Female Adults',
  adultFemale: 'Female Adults',
  TeenMale: 'Male Teenagers',
  teenMale: 'Male Teenagers',
  TeenFemale: 'Female Teenagers',
  teenFemale: 'Female Teenagers',
  ChildMale: 'Male Children',
  childMale: 'Male Children',
  ChildFemale: 'Female Children',
  childFemale: 'Female Children',
};

function prettifyKey(key) {
  const withSpaces = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
  if (!withSpaces) return key;
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
}

function formatSurveyValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return JSON.stringify(value);
}

function toSurveyDetails(survey, labels) {
  if (!survey || typeof survey !== 'object' || Array.isArray(survey)) return null;

  const details = {};
  for (const [key, value] of Object.entries(survey)) {
    if (value === null || value === undefined || value === '') continue;
    details[labels[key] ?? prettifyKey(key)] = formatSurveyValue(value);
  }
  return Object.keys(details).length > 0 ? details : null;
}

function SurveySection({ title, survey, labels }) {
  const details = toSurveyDetails(survey, labels);
  if (details === null) {
    return <p className="py-4 text-sm text-gray-500">No {title.toLowerCase()} was found for this evaluation.</p>;
  }
  return <DetailList title={title} details={details} />;
}

function SelfReport({ evaluation }) {
  return (
    <div className="space-y-6">
      <SurveySection title={"Pre-Eval Survey"} survey={evaluation?.pre_eval_survey} labels={PRE_SURVEY_LABELS} />
      <SurveySection title={"Post-Eval Survey"} survey={evaluation?.post_eval_survey} labels={POST_SURVEY_LABELS} />
    </div>
  );
}

function CloudPatientInfo({ client }) {
  if (!client) {
    return <p className="py-4 text-sm text-gray-500">No client information available.</p>;
  }
  return (
    <DetailList
      title={"Personal details"}
      details={{
        'Name': client.Name,
        'Date of Birth': client.DateOfBirth,
        'Sex': SEX_LABELS[client.Sex] ?? client.Sex,
        'Ethnicity': ETHNICITY_LABELS[client.Ethnicity] ?? client.Ethnicity,
        'Hispanic or Latino?': client.HispanicLatino ? 'Yes' : 'No',
      }}
    />
  );
}

function ChartsUnavailableNotice({ children }) {
  return (
    <div className="rounded-md bg-amber-50 p-4 text-amber-800 text-sm">
      {children}
    </div>
  );
}

export default function CloudEvalAnalysis({ client, evaluation, masterResults }) {
  const [currentTab, setCurrentTab] = useState('Normalized');

  const selectedCase = useMemo(() => toLegacySelectedCase(evaluation), [evaluation]);
  const legacyMaster = useMemo(() => toLegacyMaster(masterResults), [masterResults]);

  const chartData = useMemo(() => {
    if (!selectedCase || !hasUsableMaster(legacyMaster)) return null;
    try {
      return makeChartData({ selectedCase: selectedCase, master: legacyMaster });
    } catch (err) {
      console.error('Failed to build chart data for cloud eval:', err);
      return null;
    }
  }, [selectedCase, legacyMaster]);

  const [rawData, normalizedData] = chartData ?? [null, null];

  function getChartWidget(data) {
    if (!selectedCase) {
      return (
        <ChartsUnavailableNotice>
          This evaluation has no UVF analysis data, so charts cannot be shown.
        </ChartsUnavailableNotice>
      );
    }
    if (!data) {
      return (
        <ChartsUnavailableNotice>
          Master comparison data is unavailable for this client sex. Charts are hidden until master data is loaded.
        </ChartsUnavailableNotice>
      );
    }
    return <EvalGraphsDisplay data={data} />;
  }

  function getWidget(currentTab) {
    switch (currentTab) {
      case 'Normalized':
        return getChartWidget(normalizedData);
      case 'Raw':
        return getChartWidget(rawData);
      case 'Self-Report':
        return <SelfReport evaluation={evaluation} />;
      case 'Patient Info':
        return <CloudPatientInfo client={client} />;
      case 'Gaze Heatmap':
        return <GazeHeatmapViewer evaluation={evaluation} />;
      case 'Report':
        return (
          <CloudEvalReport
            client={client}
            evaluation={evaluation}
            masterResults={masterResults}
            normalizedData={normalizedData}
          />
        );
      default:
        return getChartWidget(normalizedData);
    }
  }

  return (
    <EvalDisplayShell
      tabs={tabs}
      currentTab={currentTab}
      setCurrentTab={setCurrentTab}
      showResultsForm={currentTab !== 'Report'}
      title={makeCloudEvalTitle(client, evaluation)}
      submissionMeta={{
        clientId: client?.id,
        evaluationId: evaluation?.id,
        caseNum: resolveCaseNumber(evaluation),
        source: 'cloud_eval'
      }}
    >
      {/* Internal notes (own cloud_eval_notes doc) sit outside the tabs so drafts survive tab switches. Never passed to the report. */}
      {evaluation?.id && <EvalNotesPanel evaluationId={evaluation.id} key={evaluation.id} />}
      {getWidget(currentTab)}
    </EvalDisplayShell>
  );
}
